/**
 * 물류 (시안) — 면 셋: 요청함 · 구매 목록(입고) · 출고. 가짜 데이터, 저장 안 됨 (2026-10-06).
 *   요청함     영업이 낸 세팅 · 출고 · 직송 요청 큐 (슬랙 "제품-요청" 대체)         ROADMAP R1
 *   구매 목록  입고 메뉴의 첫 탭(/inventory/receive). 받을 PO 줄 한 행씩 → [입고 등록] 이 재고 등록을 연다   A5 · 결정 ⑥ ⑦
 *   출고       판매전표 줄 기준. 시리얼 스캔 대조(맞음 · 틀림) · 증적 사진 · 직송 체크       A8 · 요구 9
 * 메뉴(MENU_GROUPS)에 있는 면은 머리글 · 탭 줄을 메뉴에서 받고, 없는 면(요청함)은 옛 물류 탭 줄 그대로.
 */

import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { ROW_NO_HEAD, rowNoCell } from '../components/row-no';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { escapeHtml } from '../lib/cells';
import { PREVIEW } from '../data/preview-inventory';
import type { PreviewPoLine, PreviewRequest, PreviewSoLine } from '../data/preview-inventory';

export type LogisticsTab = 'requests' | 'receive' | 'dispatch';

/** 면마다 주소 하나 — 메뉴 자리 찾기 · 옛 물류 탭 줄 둘 다 이걸로 */
const LOGISTICS_HREF: Record<LogisticsTab, string> = { requests: '/preview/logistics', receive: '/inventory/receive', dispatch: '/preview/logistics/dispatch' };

interface LogisticsData {
  tab: LogisticsTab;
  openedSo: string | null;            // 출고 — 펼친 판매전표 줄
  scanned: string;                    // 출고 — 찍은 시리얼
}

const previewBanner = (): string => `<div class="pv-banner">시안 — 가짜 데이터 · 저장 안 됨. 보고 고칠 자리를 말씀해 주세요</div>`;

export class LogisticsView extends SolaView<LogisticsData> {
  override events = {
    click: { '@open-so': 'handleOpenSo', '@dispatch': 'handleDispatch', '@request-go': 'handleRequestGo' },
    input: { '@scan': 'handleScan' },
  };

  constructor(params: { tab: LogisticsTab }) {
    super();
    this.data = { tab: params.tab, openedSo: null, scanned: '' };
  }

  override getTitle(): string {
    const place = findMenuPlace(LOGISTICS_HREF[this.data.tab]);
    return `${place === null ? '물류' : `${place.item.label} › ${place.tab.label}`} (시안) — HCLTech`;
  }

  render(): HTMLElement {
    const place = findMenuPlace(LOGISTICS_HREF[this.data.tab]);
    const heading = place === null ? '🚚 물류' : menuHeading(place);
    const tabs = place === null ? this.renderTabs() : renderMenuTabs(place);
    return htmlSolaWithInstance(this)`
      <div class="page page-logistics pv-page">
        <header class="page-header">
          <h1>${heading} <small class="pv-tag">시안</small></h1>
          ${tabs}
        </header>
        ${previewBanner()}
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  private renderTabs(): string {
    const tab = (key: LogisticsTab, label: string): string => `<a class="ic-tab${this.data.tab === key ? ' active' : ''}" data-link href="${LOGISTICS_HREF[key]}">${label}</a>`;
    const waiting = PREVIEW.REQUESTS.filter((request) => request.status !== '끝').length;
    return `<nav class="ic-tabs">${tab('requests', `요청함 <span class="chip chip-warning">${waiting}</span>`)}${tab('receive', '입고')}${tab('dispatch', '출고')}</nav>`;
  }

  private renderBody(): string {
    if (this.data.tab === 'requests') return this.renderRequests();
    if (this.data.tab === 'receive') return this.renderReceive();
    return this.renderDispatch();
  }

  // ─── 요청함 ───
  private renderRequests(): string {
    const row = (request: PreviewRequest, index: number): string => `
      <tr class="${request.status === '끝' ? 'pv-done' : ''}">
        ${rowNoCell(0, index)}
        <td class="pl-main"><span class="chip ${request.kind === '세팅' ? 'chip-primary' : request.kind === '출고' ? 'chip-success' : 'chip-warning'}">${request.kind}</span></td>
        <td class="pl-main pl-desc"><div class="pl-clamp">${escapeHtml(request.items)}</div></td>
        <td class="pl-main pl-qty"><span class="mono">${request.qty}</span></td>
        <td class="pl-main"><span class="chip ${request.status === '대기' ? 'chip-warning' : request.status === '진행' ? 'chip-primary' : 'chip-muted'}">${request.status}</span></td>
        <td>${request.status === '끝' ? '' : `<button type="button" class="btn-ghost" data-action="request-go" data-kind="${request.kind}">${request.kind === '세팅' ? '구성 열기' : request.kind === '출고' ? '출고로' : '입고로'}</button>`}</td>
        <td class="pl-sub">${escapeHtml(request.customer)}</td>
        <td class="pl-sub mono">${request.quote}</td>
        <td class="pl-sub">${escapeHtml(request.requested_by)} · <span class="mono">${request.requested_at}</span></td>
        <td class="pl-sub pl-desc"><div class="pl-clamp">${escapeHtml(request.note)}</div></td>
      </tr>`;
    return `
      <p class="pv-lead">영업이 견적 줄에서 낸 요청이 여기 쌓인다. 제품팀은 위에서부터 — 세팅은 구성 페이지로, 출고 · 직송은 그 탭으로 이어진다.</p>
      <table class="pv-table pv-po">
        <thead><tr>${ROW_NO_HEAD}<th>종류</th><th>품목</th><th>수량</th><th>상태</th><th></th><th>고객</th><th>견적</th><th>요청</th><th>메모</th></tr></thead>
        <tbody>${PREVIEW.REQUESTS.map((request, index) => row(request, index)).join('')}</tbody>
      </table>`;
  }

  // ─── 입고 ───
  /** 입고 — 구매 줄(발주서) 목록. [입고] 를 누르면 그 줄의 카드가 깔린 재고 등록으로 간다 (현민 2026-10-08 "출고처럼 구매 완료된 리스트") */
  private renderReceive(): string {
    return `
      <p class="pv-lead">구매가 끝나 받을 줄 — 발주서(PO) 한 줄 = 한 행. [입고 등록] 을 누르면 재고 등록(바코드 스캔)이 그 줄의 구매 품목 카드를 깔고 열린다. 받은 장마다 실물 한 줄이 생기고 "검수 중" 으로 시작한다.</p>
      <table class="pv-table pv-po">
        <thead><tr>${ROW_NO_HEAD}<th>파트넘버</th><th>품목</th><th>받음 / 수량</th><th></th><th>발주서</th><th>업체</th><th>단가</th></tr></thead>
        <tbody>${PREVIEW.PO_LINES.map((line, index) => this.renderPoRow(line, index)).join('')}</tbody>
      </table>`;
  }

  /** 발주서 줄 하나 — 본문: 파트넘버 · 품목 · 받음/수량, 보조: 발주서 · 업체 · 단가. [입고 등록] 은 덜 받은 줄에만 */
  private renderPoRow(line: PreviewPoLine, no: number): string {
    const isDone = line.received >= line.qty;
    return `
      <tr>
        ${rowNoCell(no, 0)}
        <td class="pl-main mono"><b>${line.option_part}</b></td>
        <td class="pl-main pl-desc"><div class="pl-clamp">${escapeHtml(line.description)}${line.is_server ? ' <span class="text-muted">· 서버 한 대</span>' : ''}</div></td>
        <td class="pl-main pl-qty ${isDone ? 'is-out' : ''}"><span class="mono">${line.received} / ${line.qty}</span></td>
        <td>${isDone ? '<span class="chip chip-muted">끝</span>' : `<a class="btn-ghost" data-link href="/inventory/new/server/purchase/${line.po}">입고 등록</a>`}</td>
        <td class="pl-sub mono">${line.po}</td>
        <td class="pl-sub">${escapeHtml(line.supplier)}</td>
        <td class="pl-sub mono">${line.unit_cost.toLocaleString()} ${line.currency}</td>
      </tr>`;
  }

  // ─── 출고 ───
  private renderDispatch(): string {
    return `
      <p class="pv-lead">판매전표 줄 기준. 시리얼을 찍으면 그 줄에 세팅된 실물과 맞는지 보여 주고, 맞을 때만 출고된다. 직송 줄은 실물이 입고 때 이미 "출고됨" 으로 생겨 여기선 확인만.</p>
      <table class="pv-table pv-po">
        <thead><tr>${ROW_NO_HEAD}<th>파트넘버</th><th>품목</th><th>나감 / 수량</th><th></th><th>고객</th><th>전표</th><th>견적</th></tr></thead>
        <tbody>${PREVIEW.SO_LINES.map((line, index) => this.renderSoRow(line, index)).join('')}</tbody>
      </table>`;
  }

  private renderSoRow(line: PreviewSoLine, no: number): string {
    const key = `${line.so}/${line.option_part}`;
    const isOpen = this.data.openedSo === key;
    const isDone = line.dispatched >= line.qty;
    const head = `
      <tr class="${isOpen ? 'pv-open' : ''} ${isDone ? 'pv-done' : ''}">
        ${rowNoCell(no, 0)}
        <td class="pl-main mono"><b>${line.option_part}</b></td>
        <td class="pl-main pl-desc"><div class="pl-clamp">${escapeHtml(line.description)}${line.is_drop_ship ? ' <span class="chip chip-warning">직송</span>' : ''}</div></td>
        <td class="pl-main pl-qty ${isDone ? 'is-out' : ''}"><span class="mono">${line.dispatched} / ${line.qty}</span></td>
        <td>${isDone ? '<span class="chip chip-muted">끝</span>' : `<button type="button" class="btn-ghost" data-action="open-so" data-key="${key}">${isOpen ? '접기' : '출고'}</button>`}</td>
        <td class="pl-sub">${escapeHtml(line.customer)}</td>
        <td class="pl-sub mono">${line.so}</td>
        <td class="pl-sub mono">${line.quote}</td>
      </tr>`;
    if (!isOpen) return head;
    return head + `<tr class="pv-detail-row"><td colspan="7">${this.renderDispatchForm(line)}</td></tr>`;
  }

  private renderDispatchForm(line: PreviewSoLine): string {
    const scanned = this.data.scanned.trim();
    const isMatch = scanned !== '' && line.reserved_serials.includes(scanned);
    const verdict = scanned === '' ? '<span class="text-muted">시리얼을 찍으세요</span>' : isMatch ? `<span class="chip chip-success">맞음 — ${scanned} 는 이 줄에 세팅된 실물</span>` : `<span class="chip pv-bad">틀림 — ${escapeHtml(scanned)} 는 이 줄의 실물이 아닙니다(세팅된 것: ${line.reserved_serials.join(', ') || '없음'})</span>`;
    return `
      <div class="pv-form">
        <div class="pv-form-col">
          <h4>세팅된 실물 ${line.reserved_serials.length}장</h4>
          ${line.reserved_serials.map((serial) => `<div class="mono">${serial}</div>`).join('') || '<div class="text-muted">세팅된 실물이 없습니다 — 요청함에서 세팅 먼저</div>'}
          <label>시리얼 스캔 <input type="text" class="pv-scan" data-action="scan" value="${escapeHtml(this.data.scanned)}" placeholder="바코드"></label>
          <div class="pv-verdict">${verdict}</div>
        </div>
        <div class="pv-form-col">
          <h4>증적 사진</h4>
          <div class="pv-photo-box">📷 포장 · 송장 · 라벨</div>
          <label>송장 번호 <input type="text" placeholder="택배 · 퀵"></label>
          <button type="button" class="btn-primary" data-action="dispatch" ${isMatch ? '' : 'disabled'}>출고 (reserved → dispatched)</button>
        </div>
      </div>`;
  }

  // ─── 핸들러 ───
  handleOpenSo(_e: Event, el: HTMLElement): void {
    const key = el.dataset.key ?? '';
    this.data.openedSo = this.data.openedSo === key ? null : key;
    this.data.scanned = '';
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  handleScan(_e: Event, el: HTMLElement): void {
    this.data.scanned = (el as HTMLInputElement).value;
    const form = this._rootElement?.querySelector('.pv-detail-row');
    const line = PREVIEW.SO_LINES.find((candidate) => `${candidate.so}/${candidate.option_part}` === this.data.openedSo);
    if (form === null || form === undefined || line === undefined) return;
    const cell = form.querySelector('td');
    if (cell === null) return;
    cell.innerHTML = this.renderDispatchForm(line);
    const input = cell.querySelector<HTMLInputElement>('.pv-scan');
    if (input !== null) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
  }

  handleDispatch(): void {
    window.alert('시안: 출고 — 실제는 그 실물 reserved → dispatched + 사진 + 전표 줄 진행');
  }

  handleRequestGo(_e: Event, el: HTMLElement): void {
    const kind = el.dataset.kind ?? '';
    window.alert(`시안: "${kind}" 요청 → ${kind === '세팅' ? '구성 페이지(재고 등록 ③)' : kind === '출고' ? '출고 탭의 그 줄' : '입고 탭의 그 구매 줄 → 재고 등록'} 로 이어진다`);
  }
}
